import { Skeleton } from "@mui/material";
import { hasModule } from "csi/_base/modules";
import { getCommentLocationParameter } from "csi/model/comment";
import { ChatCard } from "csi/notification/ChatCard";
import { MessageCard } from "csi/notification/MessageCard";
import { TaskCard } from "csi/notification/TaskCard";
import { localStore, routerStore, sessionStore } from "csi/state/rootStore";
import { C8Node, ICache } from "csi/typings";
import { getLocationForUrl, LOCATION_URL, VALID_LOCATION_KEYS } from "csi/utility/routerUtility";
import { Box, FlexColumn, FlexRow, FlexRowCenter, Text } from "designSystem/index";
import { ILocaleProvider } from "designSystem/locale";
import { red } from "designSystem/styles/color";
import _ from "lodash";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import React, { ReactElement, ReactNode, createContext, useContext } from "react";
import { NotificationNode, clearNotification, getCamundaAndProofingTasks, getMessagesForCurrentUser, getCommentsWithMentionsUrls, getNotifications, updateNotifications } from "./notificationService";

export const SkeletonComponent = (
	<FlexRowCenter
		gap={4}
		height={100}
		key="notification_skeleton_key"
		my={4}
		py={4}
	>
		<Skeleton
			height={100}
			variant="rectangular"
			width={100}
		/>
		<FlexColumn gap={4}>
			<Skeleton
				height={16}
				sx={{ borderRadius: 3 }}
				variant="rectangular"
				width={320}
			/>
			<Skeleton
				height={16}
				sx={{ borderRadius: 3 }}
				variant="rectangular"
				width={150}
			/>
		</FlexColumn>
	</FlexRowCenter>
);

const NOTIFICATION_PAGINATION_SIZE = 20;

export class NotificationStore {
	readonly cache: ICache;
	readonly elem: HTMLElement;
	/** number of notifications already fetched */
	fetchedNotifications: number = 0;
	/** are we loading the first fetch? */
	@observable loadingFirstFetch: boolean = false;
	readonly locale: ILocaleProvider;
	/** are there more notifications to load? */
	@observable moreNotificationsToLoad = true;
	@observable notifications: NotificationNode[] = [];
	@observable private _unreadOnlyChecked: boolean = localStore.notificationsUnreadOnly;
	@observable private _userSelectedIndex: number = null;

	/** all chat notifications */
	@computed get allChatNotifications(): NotificationNode[] {
		return getCommentsWithMentionsUrls(this.notifications);
	}

	/** all task notifications */
	@computed get allTaskNotifications(): NotificationNode[] {
		return getCamundaAndProofingTasks(this.notifications);
	}

	/** all message notifications */
	@computed get allMessageNotifications(): NotificationNode[] {
		return getMessagesForCurrentUser(this.notifications);
	}

	/** the contents of the conversation tab */
	@computed get chatsContent(): ReactElement[] | null {
		const { allChatNotifications, cache, handleClickAway, unreadOnlyChecked } = this;
		const chatsContent: ReactElement[] = [];
		const chats = [];

		allChatNotifications.forEach((notification, key) => {
			const chatCnl = notification.contextCNL;
			const isUnread = notification.readAt === 0;
			const canShow = !unreadOnlyChecked || (isUnread);
			if (canShow) {
				const commentLocationParameter = getCommentLocationParameter(chatCnl, cache);
				const chatProps = { id: notification.id };
				if (commentLocationParameter) {
					chats.push({ href: getLocationForUrl(null, commentLocationParameter), key, chatProps });
				} else {
					chats.push({ href: null, key, chatProps });
				}
			}
		});

		// Generate Chat content
		chats.forEach((chat): void => {
			const { href, key, chatProps } = chat;
			chatsContent.push((
				<a
					href={href}
					key={key}
					onClick={() => handleClickAway(chatProps.id)}
				>
					<ChatCard {...chatProps} />
				</a>
			));
		});

		return chatsContent;
	}

	/** the contents of the messages tab */
	@computed get messagesContent(): ReactElement[] | null {
		const { allMessageNotifications, handleClickAway, unreadOnlyChecked } = this;
		const messagesContent: ReactElement[] = [];
		const messages = [];

		allMessageNotifications.forEach((notification, key) => {
			const messageCnl = notification.contextCNL;
			const isUnread = notification.readAt === 0;
			const canShow = !unreadOnlyChecked || (isUnread);
			if (canShow) {
				const locationParameters = this.getNotificationLocationParameters(notification);
				const messageProps = { id: notification.id };
				if (locationParameters) {
					messages.push({ href: getLocationForUrl(messageCnl, locationParameters), key, messageProps });
				} else {
					messages.push({ href: messageCnl, key, messageProps });
				}
			}
		});

		// Generate message content
		messages.forEach((message): void => {
			const { href, key, messageProps } = message;
			messagesContent.push((
				<a
					href={href}
					key={key}
					onClick={() => handleClickAway(messageProps.id)}
				>
					<MessageCard {...messageProps} />
				</a>
			));
		});

		return messagesContent;
	}

	/** 
	 * Get the location parameters for the notification.
	 */
	getNotificationLocationParameters(notification: NotificationNode): Record<string, string> {
		const locationsParams: Record<string, string> = {};
		locationsParams[LOCATION_URL] = notification.contextCNL;
	
		const { targetLocation } = notification;
		if (targetLocation) {
			// Split the string by commas to get key-value pairs
			const pairs = targetLocation.split(",");
	
			pairs.forEach((pair) => {
				const [key, value] = pair.split(":");
				const trimmedKey = key.trim();
				const trimmedValue = value.trim();
	
				// Only add to result if the key is valid
				if (VALID_LOCATION_KEYS.includes(trimmedKey)) {
					locationsParams[trimmedKey] = trimmedValue;
				}
			});
		}
	
		return locationsParams;
	}
	

	/** the current items of the current display tab */
	@computed get currentItems() {
		const { isTabTasksSelected, isTabChatSelected, allTaskNotifications, allChatNotifications, allMessageNotifications } = this;
		if (isTabTasksSelected) {
			return allTaskNotifications;
		} else if (isTabChatSelected) {
			return allChatNotifications;
		} else {
			return allMessageNotifications;
		}
	}

	/** indicate if there's any unread conversation notification */
	@computed get hasUnreadConversation(): boolean {
		return this.allChatNotifications.some((notification, key) => notification.readAt === 0);
	}

	/** indicate if there's any unread task notification */
	@computed get hasUnreadTask(): boolean {
		return this.allTaskNotifications.some((notification, key) => notification.readAt === 0);
	}

	/** indicate if there's any unread messages notification */
	@computed get hasUnreadMessage(): boolean {
		return this.allMessageNotifications.some((notification, key) => notification.readAt === 0);
	}

	/** indicate if the task tab is selected */
	get isTabTasksSelected(): boolean {
		return this.selectedTabIndex === 0;
	}

	/** indicate if the task tab is selected */
	get isTabChatSelected(): boolean {
		return this.selectedTabIndex === 1;
	}

	/** the label component of conversation tab */
	@computed get labelConversations(): ReactNode {
		const { redDot } = this;

		return (
			<FlexRow>
				<Text res="Conversations" />
				{sessionStore.hasNewConversationNotifications &&
					redDot
				}
			</FlexRow>
		);
	}

	/** the label component of task tab */
	@computed get labelNotificationTask(): ReactNode {
		const { redDot } = this;

		return (
			<FlexRow>
				<Text res="NotificationTask" />
				{sessionStore.hasNewTaskNotifications &&
					redDot
				}
			</FlexRow>
		);
	}

	/** the label component of conversation tab */
	@computed get labelMessages(): ReactNode {
		const { redDot } = this;

		return (
			<FlexRow>
				<Text res="Messages" />
				{sessionStore.hasNewMessageNotifications &&
					redDot
				}
			</FlexRow>
		);
	}

	/** the dictionary of notification, id is the key */
	get notificationDictionary(): _.Dictionary<NotificationNode> {
		return _.keyBy<NotificationNode>(this.notifications, (x) => x.id);
	}

	/** the red dot component */
	@computed get redDot(): ReactNode {
		return (
			<Box pl={1}>
				<Text
					fontSize={16}
					lineHeight={12}
					sx={{
						color: red[600],
					}}
					text="●"
				/>
			</Box>
		);
	}

	/** selected tab index */
	get selectedTabIndex(): number {
		const { hasNewConversationNotifications, hasNewTaskNotifications,  hasNewMessageNotifications } = sessionStore;
		const { userSelectedIndex, tasksActive } = this;

		// select chat tab if tasks are not active
		if (!tasksActive) {
			return 0;
		}

		if (userSelectedIndex != null) {
			return userSelectedIndex;
		}

		if (hasNewTaskNotifications) {
			// if we have both task and conversation notifications, or we only have task notifications, switch to the task tab
			return 0;
		} else if (hasNewConversationNotifications) {
			return 1;
		} else if (hasNewMessageNotifications) {
			return 2;
		}

		// no new notifications, select the last visited one
		return localStore.notificationTab;
	}


	/**
	 * selected tab index and selected panel index are not the same
	 * selected tab index is 0 or 1 regardless of index assigned to each individual panel
	 * if tasks are not present, there are only one tab, and tab index must be 0, but
	 * to select conversation tab panel selectedPanelIndex must be 1 to match index assigned to that panel in our code below
	 */
	get selectedPanelIndex(): number {
		const { selectedTabIndex, tasksActive } = this;
		return tasksActive? selectedTabIndex : 1;
	}

	/** indicate if need to show the placeholder of "all caught up" */
	get showAllCaughtUp(): boolean {
		const { hasUnreadConversation, hasUnreadTask, isTabTasksSelected, isTabChatSelected, unreadOnlyChecked, hasUnreadMessage } = this;
		if (isTabTasksSelected) {
			return unreadOnlyChecked && !hasUnreadTask;
		} else if (isTabChatSelected) {
			return unreadOnlyChecked && !hasUnreadConversation;
		} else {
			return unreadOnlyChecked && !hasUnreadMessage;
		}
	}

	/** indicate if need to show the "mark all read" button */
	get showMarkAllRead(): boolean {
		const { hasUnreadConversation, hasUnreadTask, isTabTasksSelected, isTabChatSelected, hasUnreadMessage } = this;
		if (isTabTasksSelected) {
			return hasUnreadTask;
		} else if (isTabChatSelected) {
			return hasUnreadConversation;
		} else {
			return hasUnreadMessage;
		}
	}

	/** indicate whether or not the task tab is active */
	get tasksActive(): boolean {
		const hasCamunda = hasModule("Camunda");
		const hasProofing = hasModule("Proofing");

		return hasCamunda || hasProofing;
	}

	/** the content of tasks tab */
	@computed get tasksContent(): ReactElement[] | null {
		const { allTaskNotifications, cache, handleClickAway, unreadOnlyChecked } = this;
		const tasksContent: ReactElement[] = [];

		allTaskNotifications.sort((a, b) => {
			return a.createdAt > b.createdAt ? -1 : 1;
		});

		const { userUrl } = sessionStore;
		allTaskNotifications.forEach((notification, key) => {
			const TaskNode = cache.get(notification.contextCNL);
			const isAssignedTask = TaskNode.Assignee == userUrl;

			let href = "";
			const locationParams = {
				URL: "centric://REFLECTION/INSTANCE/_CS_Tasks/null",
				Tab: (isAssignedTask ? "MyAssignedTasks" : "MyCandidateTasks"),
				Filmstrip: notification.contextCNL,
			};

			href = getLocationForUrl(null, locationParams);
			const isUnread = notification.readAt === 0;
			const canShow = !unreadOnlyChecked || (isUnread);

			if (canShow) {
				const c8Instance = cache.get(TaskNode.C8Instance);

				if (c8Instance) {
					const taskProps = {
						id: notification.id,
						task: TaskNode,
						isAssignedTask: isAssignedTask,
					};

					tasksContent.push((
						<a
							href={href}
							key={key}
							onClick={() => handleClickAway(notification.id)}
						>
							<TaskCard {...taskProps} />
						</a>
					));
				}
			}
		});

		return tasksContent;
	}

	/** dropdown title */
	get title(): string {
		return this.locale.resource("NotificationsTitle");
	}

	/** indicate if the unreadOnly is selected */
	get unreadOnlyChecked(): boolean {
		return this._unreadOnlyChecked;
	}

	/** the index that user selects */
	get userSelectedIndex(): number | null {
		return this._userSelectedIndex;
	}

	/** get the initiator node */
	getInitiatorNode = (initiatorCNL: string): C8Node => {
		return this.cache.get(initiatorCNL);	
	};

	/** get the specific notification by id */
	getById = (id: number): NotificationNode => {
		return this.notificationDictionary[id];
	};

	/** set notification read */
	handleClickAway = (id: number): void => {
		updateNotifications([id], true);
		this.handleOnClose();
	};

	/**  close dropdown */
	handleOnClose = (): void => {
		routerStore.setBodyComponent(null);
	};

	/** fetch notifications */
	@action fetchNotifications = async (): Promise<void> => {
		const { cache, fetchedNotifications, unreadOnlyChecked } = this;
		const nodePaths = [
			"Child:Author",
			"Child:C8Instance",
			"Child:__Parent__/Child:__Parent__/Child:NamedParent",
		];
		const notificationResponse = await getNotifications(unreadOnlyChecked, nodePaths, fetchedNotifications, NOTIFICATION_PAGINATION_SIZE);
		const { notificationNodes, resultNodes, supportNodes } = notificationResponse;
		if (notificationNodes.length) {
			this.fetchedNotifications += notificationNodes.length;
			cache.setData([...(resultNodes || []), ...(supportNodes || [])]);
			runInAction(() => {
				this.notifications = [...this.notifications, ...notificationNodes];
				this.moreNotificationsToLoad = true;
			});
		} else {
			runInAction(() => {
				this.moreNotificationsToLoad = false;
			});
		}
		runInAction(() => {
			this.loadingFirstFetch = false;
		});
		clearNotification();
	};

	/** handler for the tab changing */
	@action handleTabsChange = ((event: any, newValue: number): void => {
		const currentPanelIndex = this.selectedPanelIndex;
		this._userSelectedIndex = newValue;

		if (currentPanelIndex === 0) {
			sessionStore.setHasNewTaskNotifications(false);
		} else if (currentPanelIndex === 1) {
			sessionStore.setHasNewConversationNotifications(false);
		} else {
			sessionStore.setHasNewMessageNotifications(false);
		}
	});

	/** mark the current items on the active tab read */
	@action markAllReadClick = (): void => {
		const { currentItems, setRead } = this;
		const ids = [];
		const currentTime = Date.now();

		currentItems.forEach((item) => {
			if (item.readAt == 0) {
				setRead(item.id, true);
				item.readAt = currentTime;
				ids.push(item.id);
			}
		});

		updateNotifications(ids, true);
	};

	/** the handler for unRead toggle button */
	@action onUnreadChange = (event: object, value: boolean): void => {
		this._unreadOnlyChecked = value;
		localStore.notificationsUnreadOnly = value;
		// if changing the value, reset notifications
		this.notifications = [];
		this.loadingFirstFetch = true;
		this.fetchedNotifications = 0;
		this.fetchNotifications();
	};

	/** set loading first fetch */
	@action setLoadingFirstFetch = (loading: boolean): void => {
		this.loadingFirstFetch = loading;
	};

	/** set the specific notification read/unread */
	@action setRead = (id: number, read: boolean): void => {
		const targetNotification = this.notificationDictionary[id];
		targetNotification.readAt = read ? Date.now() : 0;
	};

	/** need to reset the open status, this function would get called when this store is destroyed  */
	onCleanup = (store: NotificationStore) => {
		sessionStore.setNotificationDropdownIsOpen(false);

		// reset the status
		const { selectedPanelIndex } = store;

		if (selectedPanelIndex === 0) {
			sessionStore.setHasNewConversationNotifications(false);
		} else if (selectedPanelIndex === 1) {
			sessionStore.setHasNewTaskNotifications(false);
		} else {
			sessionStore.setHasNewMessageNotifications(false);
		}
	};

	constructor(
		elem: HTMLElement,
		cache: ICache,
		locale: ILocaleProvider,
	) {
		makeObservable(this);
		this.elem = elem;
		this.cache = cache;
		this.locale = locale;

		// C8-54812 if there are no tasks - tasks tab can't be default
		if (!this.tasksActive && (localStore.notificationTab === 0)) {
			this.handleTabsChange(null, 1);
		}

		sessionStore.setNotificationDropdownIsOpen(true);
	}
}

export const NotificationStoreContext = createContext<NotificationStore>(undefined);
export const useNotificationStore = () => useContext(NotificationStoreContext);